import { useCallback, useMemo } from 'react';
import { skipToken } from '@tanstack/react-query';

import { useQuery } from 'Services/query/useQuery';
import { getGiftLinks } from 'Services/api/giftLink/getGiftLinks';
import { Modals } from 'Services/Modals';
import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';

import { getAvailableGiftTotal } from '../../utils';
import { GiftAndShareModal } from '../../modals/GiftAndShareModal';

import type { ArticleTypeT } from 'Constants/articleTypes';
import type { GiftArticleT } from 'Shared/articles/types';

type PropsT = {
  article: GiftArticleT;
  articleType: ArticleTypeT;
  isMpArticle?: boolean;
  nodeRefToRestoreFocus?: React.RefObject<HTMLElement>;
};

export const useGiftArticle = ({
  article,
  articleType,
  isMpArticle,
  nodeRefToRestoreFocus,
}: PropsT) => {
  const { userId } = useAuthorizedUser();

  const { isPending, data } = useQuery({
    queryKey: ['shared-links', userId],
    queryFn: userId ? getGiftLinks : skipToken,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const availableGiftTotal = getAvailableGiftTotal(data);

  const availableGift = useMemo(() => {
    return (
      data?.find(({ subjectId }) => {
        return subjectId === Number(article.id);
      }) ?? null
    );
  }, [article.id, data]);

  const openModal = useCallback(() => {
    if (isPending) {
      return;
    }
    Modals.appendModal(
      ({ ariaDialogLabelId }) => {
        return (
          <GiftAndShareModal
            ariaDialogLabelId={ariaDialogLabelId}
            article={article}
            articleType={articleType}
            availableGift={availableGift}
            availableGiftTotal={availableGiftTotal}
            isMpArticle={isMpArticle}
          />
        );
      },
      {
        hideNavigation: true,
        hideOnPrint: true,
        nodeRefToRestoreFocus,
      },
    );
  }, [
    article,
    articleType,
    availableGift,
    availableGiftTotal,
    isPending,
    isMpArticle,
    nodeRefToRestoreFocus,
  ]);

  return {
    availableGift,
    availableGiftTotal,
    isLoading: isPending,
    openModal,
  };
};
